/* eslint-disable @typescript-eslint/no-namespace -- we use namespaces because previously we used named exports but the generated api type complexity grew to the point where tsc threw error 7056 */
import { ThunkDispatch } from 'redux-thunk';

import { enhancedApi } from './enhancedApi';

export * from './generated';
export const api = enhancedApi;
export { initialize } from './config';
export * from './middleware';
export * from './types';
export * from './libs';

/** How long an SSR render will wait for its queries before giving up on the slow ones. */
export const defaultOperationsTimeoutMs = 10000;

/**
 * Waits for all running queries to settle, but never longer than `timeoutMs`.
 *
 * Awaited inside `getServerSideProps`, where an unbounded wait would let one hung query hold a
 * rendering instance indefinitely. `allSettled` keeps a rejected query from aborting the wait for the
 * others; failures are already recorded in the store by `queryErrorMiddleware`. A query past the
 * timeout keeps running and resolves on the client.
 */
export const waitForAllOperations = async (
  dispatch: ThunkDispatch<any, any, any>,
  timeoutMs = defaultOperationsTimeoutMs
) => {
  const operations = Promise.allSettled(dispatch(api.util.getRunningQueriesThunk()));

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      console.warn(`[api] Timed out after ${timeoutMs}ms waiting for running queries; rendering without them.`);
      resolve();
    }, timeoutMs);
  });

  try {
    await Promise.race([operations, timeout]);
  } finally {
    clearTimeout(timer);
  }
};

export const resetApiState = () => {
  return api.util.resetApiState();
};

// re-export api hooks with clearer names
export namespace publicApi {
  export const useGetAttribute = api.useGetV1AttributeQuery;
  export const useGetAttributeEntries = api.useGetV1AttributeEntriesQuery;
  export const useGetCategories = api.useGetV1CategoryQuery;
  export const useGetDataObjects = api.useGetV1DataObjectQuery;
  export const useGetDataObjectsLazy = api.useLazyGetV1DataObjectQuery;
  export const useGetDataObjectCount = api.useGetV1DataObjectCountQuery;
  export const useGetDataObjectById = api.useGetV1DataObjectByIdQuery;
  export const usePostDataObjectByIdContact = api.usePostV1DataObjectByIdContactMutation;
  export const usePostDataObjectByIdReport = api.usePostV1DataObjectByIdReportMutation;
  export const useGetGeoLocations = api.useGetV1GeoLocationsQuery;
  export const useGetGeoRegions = api.useGetV1GeoRegionsQuery;
  export const useGetFavorites = api.useGetV1FavoriteQuery;
  export const useSaveFavorites = api.usePostV1FavoriteMutation;
  export const useDeleteFavorites = api.useDeleteV1FavoriteMutation;
  export const useGetConfigurationPlatforms = api.useGetV1ConfigurationPlatformsQuery;
  export const useGetPageContent = api.useGetV1SeoPageContentQuery;
  export const useGetSearchAlerts = api.useGetV1SearchAlertsQuery;
  export const useGetSearchAlertById = api.useGetV1SearchAlertsByIdQuery;
  export const useActivateSearchAlert = api.usePutV1SearchAlertsByIdActivateMutation;
  export const useCreateSearchAlert = api.usePostV1SearchAlertsMutation;
  export const useUpdateSearchAlert = api.usePutV1SearchAlertsByIdMutation;
  export const useDeleteSearchAlert = api.useDeleteV1SearchAlertsByIdMutation;
  export const useUpdateSearchAlertInteractionDate = api.usePutV1SearchAlertsByIdInteractionMutation;
  export const useGetAccountById = api.useGetV1AccountByIdQuery;
  export const useCreateStatisticsEntry = api.usePostV1StatisticsMutation;
  export const useGetNextIssueInfo = api.useGetV1PrintTitleNextIssueInfoQuery;
  export const useSearchAutocomplete = api.useGetV1AutocompleteQuery;
}

export namespace publicApiEndpoints {
  export const getAttribute = api.endpoints.getV1Attribute;
  export const getCategories = api.endpoints.getV1Category;
  export const getDataObjects = api.endpoints.getV1DataObject;
  export const getDataObjectCount = api.endpoints.getV1DataObjectCount;
  export const getDataObjectById = api.endpoints.getV1DataObjectById;
  export const postDataObjectByIdContact = api.endpoints.postV1DataObjectByIdContact;
  export const getPageContent = api.endpoints.getV1SeoPageContent;
  export const getSearchAlerts = api.endpoints.getV1SearchAlerts;
  export const getSearchAlertById = api.endpoints.getV1SearchAlertsById;
  export const getAccountById = api.endpoints.getV1AccountById;
  export const getFavorites = api.endpoints.getV1Favorite;
  export const getNextIssueInfo = api.endpoints.getV1PrintTitleNextIssueInfo;
  export const getPlatforms = api.endpoints.getV1ConfigurationPlatforms;
  export const getSeoUrls = api.endpoints.getV1SeoUrls;
}

export namespace accountApi {
  export const useGetAccounts = api.useGetV1AccountAccountQuery;
  export const useCreateAccount = api.usePostV1AccountAccountMutation;
  export const useGetAccountById = api.useGetV1AccountAccountByIdQuery;
  export const useUpdateAccount = api.usePutV1AccountAccountByIdMutation;
  export const useGetLoginInfo = api.useGetV1AccountAccountLoginInfoQuery;
  export const useGetLoginInfoLazy = api.useLazyGetV1AccountAccountLoginInfoQuery;
  export const useGetAttributes = api.useGetV1AccountAttributeQuery;
  export const useGetCategories = api.useGetV1AccountCategoryQuery;
  export const useGetCategory = api.useGetV1AccountCategoryByIdQuery;
  export const useGetDataObjects = api.useGetV1AccountDataObjectQuery;
  export const useGetContactConversations = api.useGetV1AccountContactMessagesQuery;
  export const useGetContactConversation = api.useGetV1AccountContactMessagesByIdQuery;
  export const useGetContactMessageCounts = api.useGetV1AccountContactMessagesCountsQuery;
  export const useMarkContactMessagesRead = api.usePutV1AccountContactMessagesReadMutation;
  export const useMarkContactConversationUnread = api.usePutV1AccountContactMessagesByIdUnreadMutation;
  export const useReplyToContactMessage = api.usePostV1AccountContactMessagesByIdReplyMutation;
  export const useCreateDataObject = api.usePostV1AccountDataObjectMutation;
  export const useGetDataObjectById = api.useGetV1AccountDataObjectByIdQuery;
  export const useUpdateDataObject = api.usePutV1AccountDataObjectByIdMutation;
  export const useGetPrintAdvertIssueDates = api.useGetV1AccountPrintAdvertIssueDatesQuery;
  export const useGetPrintAdvertById = api.useGetV1AccountPrintAdvertByIdQuery;
  export const useGetPrintAdvertPreview = api.usePostV1AccountPrintAdvertPreviewMutation;
  export const useGetPrintQuote = api.useGetV1AccountPrintAdvertQuoteQuery;
  export const useAcceptPrintQuote = api.usePostV1AccountPrintAdvertAcceptQuoteMutation;
  export const useGetConfigurationProcess = api.useGetV1AccountConfigurationProcessQuery;
  export const useGetConfigurationDataObjectTypes = api.useGetV1AccountConfigurationDataObjectTypesQuery;
  export const useGetProducts = api.useGetV1AccountProductsQuery;
  export const useGetContracts = api.useGetV1AccountContractsQuery;
  export const useCreateContract = api.usePostV1AccountContractsCreateMutation;
  export const useUpgradeContract = api.usePostV1AccountContractsUpgradeMutation;
  export const useGetPublications = api.useGetV1AccountPublicationDataObjectByIdQuery;
  export const useGetPublicationOptions = api.useGetV1AccountPublicationOptionsQuery;
  export const usePublishDataObject = api.usePostV1AccountPublicationDataObjectByIdMutation;
  export const usePublishPrintAdvert = api.usePostV1AccountPrintAdvertPublishMutation;
  export const useCalculatePrice = api.useGetV1AccountPublicationPriceQuery;
  export const useActivateDataObject = api.usePutV1AccountDataObjectByIdActivateMutation;
  export const useDeactivateDataObject = api.usePutV1AccountDataObjectByIdDeactivateMutation;
  export const useDeleteDataObject = api.useDeleteV1AccountDataObjectByIdMutation;
  export const useDuplicateDataObject = api.usePostV1AccountDataObjectByIdDuplicateMutation;
  export const useSetTopListing = api.usePutV1AccountDataObjectByIdTopListingMutation;
  export const useDataObjectCounts = api.useGetV1AccountDataObjectCountsQuery;
  export const useGetLogo = api.useGetV1AccountLogoByAccountIdQuery;
  export const useSaveLogo = api.usePostV1AccountLogoMutation;
  export const useDeleteLogo = api.useDeleteV1AccountLogoMutation;
  export const useGetLogins = api.useGetV1AccountLoginsQuery;
  export const useAddLogin = api.usePostV1AccountLoginsMutation;
  export const useRemoveLogin = api.useDeleteV1AccountLoginsByIdMutation;
  export const useSetLoginRole = api.usePutV1AccountLoginsSetRoleMutation;
  export const useGetAccountInvitations = api.useGetV1AccountLoginsInvitationsQuery;
  export const useAcceptAccountInvitation = api.usePutV1AccountLoginsAcceptInvitationMutation;
  export const useRejectAccountInvitation = api.usePutV1AccountLoginsRejectInvitationMutation;
  export const useGetAddresses = api.useGetV1AccountAddressQuery;
  export const useCreateAddress = api.usePostV1AccountAddressMutation;
  export const useUpdateAddress = api.usePutV1AccountAddressByIdMutation;
  export const useDeleteAddress = api.useDeleteV1AccountAddressByIdMutation;
  export const useGetContactPerson = api.useGetV1AccountContactPersonQuery;
  export const useCreateContactPerson = api.usePostV1AccountContactPersonMutation;
  export const useUpdateContactPerson = api.usePutV1AccountContactPersonByIdMutation;
  export const useDeleteContactPerson = api.useDeleteV1AccountContactPersonByIdMutation;
  export const useGetPaymentStatus = api.useGetV1AccountPaymentsStatusQuery;
  export const useCreatePayment = api.usePostV1AccountPaymentsCreateMutation;
  export const useGetOrder = api.useGetV1AccountPaymentsOrderQuery;
  export const useGetPaymentContract = api.useGetV1AccountPaymentsContractQuery;
  export const useCreatePrintOrder = api.usePostV1AccountPaymentsPrintOrderMutation;
  export const useGetApiKeys = api.useGetV1AccountApiKeysQuery;
  export const useCreateApiKey = api.usePostV1AccountApiKeysMutation;
  export const useRevokeApiKey = api.usePutV1AccountApiKeysByUidRevokeMutation;
  export const useGetImportExecutions = api.useGetV1AccountApiLogsImportExecutionsQuery;
  export const useGetImportExecution = api.useGetV1AccountApiLogsImportExecutionsByIdQuery;
}

export namespace accountApiEndpoints {
  export const getAccounts = api.endpoints.getV1AccountAccount;
  export const getContactMessageCounts = api.endpoints.getV1AccountContactMessagesCounts;
  export const getDataObjects = api.endpoints.getV1AccountDataObject;
}
